import React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useDispatch, useStore } from "react-redux";
import { changePassword } from "../actions/Auth";
import { colors } from "../brand";

const RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "A number", test: (p) => /[0-9]/.test(p) },
  { label: "A symbol, e.g. ! @ # $", test: (p) => /[^\w\s]/.test(p) },
];

const empty = { passwordCurrent: "", password: "", passwordConfirm: "" };

export default function ChangePassword() {
  const dispatch = useDispatch();
  const store = useStore();
  const [form, setForm] = React.useState(empty);
  const [show, setShow] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const set = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError("");
    setSuccess("");
  };

  const allRulesMet = RULES.every((r) => r.test(form.password));
  const matches = form.password && form.password === form.passwordConfirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.passwordCurrent || !form.password || !form.passwordConfirm) {
      setError("Fill in all three fields");
      return;
    }
    if (!allRulesMet) {
      setError("The new password does not meet all the rules below");
      return;
    }
    if (!matches) {
      setError("The new passwords do not match");
      return;
    }
    setBusy(true);
    await dispatch(changePassword(form));
    const { changePasswordError, changePasswordSuccess } = store.getState().auth;
    setBusy(false);
    if (changePasswordError) {
      setError(changePasswordError);
    } else {
      setSuccess(changePasswordSuccess || "Password changed");
      setForm(empty);
    }
  };

  const eye = (
    <InputAdornment position="end">
      <IconButton
        size="small"
        aria-label={show ? "Hide passwords" : "Show passwords"}
        onClick={() => setShow((v) => !v)}
        edge="end"
      >
        {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Paper
      component="form"
      onSubmit={submit}
      elevation={0}
      sx={{
        p: "20px",
        borderRadius: "14px",
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: colors.textPrimary }}>
        Change password
      </Typography>
      <Typography sx={{ fontSize: 13, color: colors.textSecondary, mb: 2 }}>
        Your new password works in every CBX app.
      </Typography>

      <Stack spacing={1.5}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <TextField
          size="small"
          label="Current password"
          type={show ? "text" : "password"}
          autoComplete="current-password"
          value={form.passwordCurrent}
          onChange={set("passwordCurrent")}
          InputProps={{ endAdornment: eye }}
        />
        <TextField
          size="small"
          label="New password"
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={form.password}
          onChange={set("password")}
        />
        <TextField
          size="small"
          label="Confirm new password"
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={form.passwordConfirm}
          onChange={set("passwordConfirm")}
          error={Boolean(form.passwordConfirm) && !matches}
          helperText={
            form.passwordConfirm && !matches ? "Does not match the new password" : " "
          }
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 0.5,
          }}
        >
          {RULES.map((r) => {
            const ok = r.test(form.password);
            return (
              <Stack key={r.label} direction="row" spacing={0.7} alignItems="center">
                {ok ? (
                  <CheckCircleIcon sx={{ fontSize: 16, color: colors.secondary }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: "#cbd5e1" }} />
                )}
                <Typography
                  sx={{ fontSize: 12.5, color: ok ? colors.textPrimary : colors.textSecondary }}
                >
                  {r.label}
                </Typography>
              </Stack>
            );
          })}
        </Box>

        <Box>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={busy}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              px: 3,
              backgroundColor: colors.primary,
              "&:hover": { backgroundColor: colors.primaryDark },
            }}
          >
            {busy ? "Changing…" : "Change password"}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
